import { Autocomplete, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

export type SelectOption = { id: string; name: string };

type Option = SelectOption & { inputValue?: string };

const filter = createFilterOptions<Option>();

/**
 * Build filtered options for a creatable single-select, exported for unit tests.
 * Appends a synthetic create option when the input doesn't exactly match any existing name.
 */
export function buildSingleSelectFilterOptions(
  options: SelectOption[],
  inputValue: string,
  createOptionLabel: (inputValue: string) => string,
): Option[] {
  const filtered = filter(options as Option[], {
    inputValue,
    getOptionLabel: (o) => o.name,
  });

  if (
    inputValue.trim() !== '' &&
    !options.some((o) => o.name.toLowerCase() === inputValue.toLowerCase())
  ) {
    filtered.push({ id: '', name: createOptionLabel(inputValue), inputValue });
  }
  return filtered;
}

/**
 * Single-value autocomplete. Mirrors `MultiSelect` but for a single value.
 *
 * When `createOptionLabel` is provided, enables inline creation mode:
 * typing a non-matching name surfaces a synthetic "create" option and
 * freeSolo lets the user confirm with Enter instead of clicking. Every
 * keystroke fires `onInputChange`; callers use it to track the typed value.
 */
export function SingleSelectCreatable<T extends SelectOption>({
  value,
  options,
  onChange,
  onInputChange,
  createOptionLabel,
  label,
  placeholder,
  helperText,
  error = false,
  disabled = false,
}: {
  value: T | null;
  options: T[];
  onChange: (value: T | null) => void;
  onInputChange?: (value: string) => void;
  createOptionLabel?: (inputValue: string) => string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
}) {
  return (
    <Autocomplete
      options={options}
      value={value}
      disabled={disabled}
      fullWidth
      freeSolo={Boolean(createOptionLabel)}
      filterOptions={
        createOptionLabel
          ? (opts, state) =>
              buildSingleSelectFilterOptions(
                opts,
                state.inputValue,
                createOptionLabel,
              ) as T[]
          : undefined
      }
      onChange={(_e, next) => {
        if (!next || typeof next === 'string') return;
        const opt = next as Option;
        // Synthetic create option — parent already has the name via onInputChange.
        if (opt.inputValue !== undefined) return;
        onChange(opt as T);
      }}
      onInputChange={
        onInputChange ? (_e, val) => onInputChange(val) : undefined
      }
      // For synthetic options, return the raw typed value so the input shows
      // "Gamma" not "Create new track 'Gamma'" after selection.
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        return (opt as Option).inputValue ?? opt.name;
      }}
      // Dropdown still shows the formatted create label via renderOption.
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <li key={key} {...rest}>
            {option.name}
          </li>
        );
      }}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}
